/**
 * Cross-platform media download utility.
 * - On web: downloads via a hidden <a download>.
 * - On Capacitor native (iOS/Android): writes the file to Documents and offers a native Share sheet
 *   so the user can save it to Photos / Files / send it elsewhere.
 *
 * Accepts either a remote URL, a data: URL, or a base64 string + mime.
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const slug = (s) => (s || '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 80);

const guessExt = (mime, fallback = 'bin') => {
  if (!mime) return fallback;
  const map = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
    'audio/mpeg': 'mp3',
    'audio/mp4': 'm4a',
    'audio/wav': 'wav',
    'audio/webm': 'webm',
  };
  return map[mime.toLowerCase()] || fallback;
};

const fetchAsBase64 = async (url) => {
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
  const blob = await resp.blob();
  const mime = blob.type || 'application/octet-stream';
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });
  // strip "data:<mime>;base64,"
  const base64 = String(dataUrl).split(',')[1] || '';
  return { base64, mime, blob };
};

const triggerWebDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
};

/**
 * @param {Object} opts
 * @param {string} [opts.url]      Remote or blob URL of the media
 * @param {string} [opts.base64]   Raw base64 (no data: prefix). If used, also pass mime.
 * @param {string} [opts.mime]     Mime type (used with base64)
 * @param {string} [opts.filename] Filename without extension (extension auto-derived from mime)
 */
export async function downloadMedia({ url, base64, mime, filename }) {
  let payload;
  if (base64) {
    payload = { base64, mime: mime || 'image/png', blob: null };
  } else if (url) {
    payload = await fetchAsBase64(url);
  } else {
    throw new Error('downloadMedia: url or base64 required');
  }

  const ext = guessExt(payload.mime);
  const baseName = slug(filename || `destine-${Date.now()}`);
  const finalName = `${baseName}.${ext}`;

  if (Capacitor.isNativePlatform()) {
    // Write to Documents then open the native share sheet (lets user save to Photos / Files).
    const writeRes = await Filesystem.writeFile({
      path: finalName,
      data: payload.base64,
      directory: Directory.Documents,
      recursive: true,
    });
    try {
      await Share.share({
        title: 'Destine',
        text: finalName,
        url: writeRes.uri,
        dialogTitle: 'Enregistrer le média',
      });
    } catch {
      // User dismissed the share sheet — file is still on disk in Documents.
    }
    return { saved: true, path: writeRes.uri };
  }

  // Web fallback
  let blob = payload.blob;
  if (!blob) {
    const byteChars = atob(payload.base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i);
    blob = new Blob([bytes], { type: payload.mime || 'application/octet-stream' });
  }
  triggerWebDownload(blob, finalName);
  return { saved: true, path: finalName };
}
